<template>
  <div class="table-wrapper tenancy-marketing-contacts">
    <div class="table-caption">
      {{ $t('tenancy.table.marketing') }}
    </div>
    <div
      v-if="allowLinkContacts"
      class="link__contact"
    >
      <button
        type="button"
        class="btn link__contact--btn"
        :disabled="!tenancyId"
        @click="linkFunc({isMarketing: true})"
      >
        {{ $t('tenancy.table.linkContact') }}
      </button>
      <span class="link__contact--plus">+</span>
    </div>
    <table>
      <thead>
        <tr>
          <th
            v-for="(header, index) in itemHeaders"
            :key="index"
            :class="header.class"
          >
            {{ header.name }}
          </th>
          <th />
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(item, index) in marketingContacts"
          :key="index"
        >
          <td class="select-roles">
            <HKLMultiSelect
              v-if="allowLinkContacts"
              class="multi-value"
              :class="deep(item, 'CompanyContact.roleType').length === 0 ? 'error-highlight-floor' : ''"
              :value="deep(item, 'CompanyContact.roleType')"
              :options="roleHklOptions"
              placeholder=""
              @change="val => updatePathField(index, 'roleType', val)"
            />
            <span v-else>{{ deep(item, 'CompanyContact.roleType').join(', ') || '--' }}</span>
          </td>
          <td class="number-column">
            <div v-if="canAccessContact">
              <a
                class="name-hyper-link"
                @click="navToContactDetail(item.id, true)"
              >{{ displayI18nFullName(item.firstName, item.lastName) || item.name }}</a>
              <i
                v-if="isEngineer"
                v-show="!!item.remarks"
                class="remark-included-flag"
              />
            </div>
            <div v-else>
              {{ displayI18nFullName(item.firstName, item.lastName) || item.name }}
            </div>
          </td>
          <td class="number-column">
            {{ item.phone | isEmptyValue }}
          </td>
          <td class="select-marketing-emails">
            <HKLMultiSelect
              v-if="allowLinkContacts"
              class="multi-value"
              :class="deep(item, 'CompanyContact.mEmails').length === 0 ? 'error-highlight-floor' : ''"
              :value="deep(item, 'CompanyContact.mEmails')"
              :options="emailHklOptions(item)"
              placeholder=""
              @change="val => updatePathField(index, 'mEmails', val)"
            />
            <span v-else>{{ deep(item, 'CompanyContact.mEmails').join(', ') || '--' }}</span>
          </td>
          <td>
            <TextField
              v-hkldinput
              parentNode=""
              :value="deep(item, 'CompanyContact.mRemarks')"
              :disabled="!allowLinkContacts"
              :readonly="!allowLinkContacts"
              @updateValue="val => updateRemarks(index, val)"
            />
          </td>
          <td class="icon-remove">
            <i
              v-if="allowLinkContacts"
              class="fa fa-times-thin"
              @click="openDeleteConfirm(index, item)"
            />
          </td>
        </tr>
      </tbody>
    </table>
    <ActionConfirmModal
      ref="deleteConfirmModal"
      :modalId="delConfModalId"
      :confirmTitle="$t('deleteConfirmMsg')"
    />
  </div>
</template>

<script>
import HKLMultiSelect from '@/controls/Select/HKLMultiSelect.vue'
import { mapGetters, mapMutations, mapActions } from 'vuex'

import ActionConfirmModal from '@/controls/ActionConfirmModal'
import TextField from '@/controls/TextField'

import mutationTypes from '@/store/mutation-types'
import { utils as formUtils } from '@/plugin/InputChange'
import formFullnameMixin from '@/pages/formFullnameMixin'
import navToContactDetailMixin from './navToContactDetailMixin'

export default {
  name: 'MarketingContact',
  components: {
    TextField,
    ActionConfirmModal,
    HKLMultiSelect,
  },
  mixins: [navToContactDetailMixin, formFullnameMixin],
  props: {
    tenancyId: [String, Number],
    allowLinkContacts: Boolean,
    linkFunc: Function,
  },
  data () {
    return {
      delConfModalId: `marketingContact-${new Date().getTime()}-${Math.floor(Math.random() * 1000)}`,
      deep,
      isEmailsEmpty: false,
      isTypeEmpty: false,
      roleOptions: [this.$t('tenancy.roleTypeOptions.marketing'),
        this.$t('tenancy.roleTypeOptions.CRM'),
        this.$t('tenancy.roleTypeOptions.PR'),
        this.$t('tenancy.roleTypeOptions.shop')],
    }
  },
  methods: {
    ...mapActions('ccd', ['loadTenancy']),
    ...mapMutations('ccd', {
      updateTenancyContactPathField: mutationTypes.TENANCY_CONTACT_UPDATE_PATH_FIELD,
    }),
    updatePathField (index, fieldName, newValue) {
      this.updateTenancyContactPathField({
        index,
        path: [fieldName],
        newValue,
      })
      formUtils.setDirty()
    },
    openDeleteConfirm (index, item) {
      this.$refs.deleteConfirmModal.open(this.removeContact, [index, item])
    },
    isMandatoryEmpty (attr) {
      return Object.keys(this.marketingContacts).some(index => deep(this.marketingContacts[index], ['CompanyContact', attr]).length === 0)
    },
    isInvalidEmailsTypes () {
      this.isEmailsEmpty = this.isMandatoryEmpty('mEmails')
      this.isTypeEmpty = this.isMandatoryEmpty('roleType')
      return this.isEmailsEmpty || this.isTypeEmpty
    },
    removeContact (index, item) {
      this.updatePathField(index, 'isMarketing', false)
      this.updatePathField(index, 'mEmails', [])
    },
    emailHklOptions (item) {
      return (item.allEmails || []).map((email) => ({ name: email, value: email }))
    },
    updateRemarks (index, val) {
      this.updatePathField(index, 'mRemarks', val)
    },
  },
  computed: {
    ...mapGetters('ccd', ['currentTenancy']),
    ...mapGetters('user', ['isTenant', 'isEngineer']),
    itemHeaders () {
      return [{
        name: this.$t('company.contactTable.role'),
        class: 'column-type',
      }, {
        name: this.$t('tenancy.table.name'),
        class: 'column-name',
      }, {
        name: this.$t('general.phone'),
        class: 'column-phone',
      }, {
        name: this.$t('general.email'),
        class: 'column-email',
      }, {
        name: this.$t('contact.remarks'),
        class: 'column-remarks',
      }]
    },
    roleHklOptions () {
      return (this.roleOptions || []).map((role) => ({ name: role, value: role }))
    },
    marketingContacts () {
      if (!(this.currentTenancy && this.currentTenancy.tenancyContacts)) return []

      return this.currentTenancy.tenancyContacts.reduce((partial, item, index) => {
        if (item.CompanyContact.isMarketing) {
          partial[index] = item
        }
        return partial
      }, {})
    },
  },
  async mounted () {
    if (this.allowLinkContacts) {
      const routeParams = this.$router.currentRoute.params
      if (!this.currentTenancy.tenancyContacts) {
        const tenancyId = routeParams.id
        await this.loadTenancy(tenancyId)
      }
    }
  },
}
</script>
<style lang="scss" scoped>
@import '~styles/tender/view';
@import '~styles/table';
@import '~styles/components/tenancy-contacts';
table .number-column {
  width: auto;
  max-width: 200px;
}
</style>

<style lang="scss">
.tenancy-marketing-contacts {
  .error-highlight-floor {
    background-color: #f9ecea;
  }
  .text-input textarea {
    width: 100%;
  }
  .select-marketing-emails {
    width: 510px;
  }
}
</style>
